import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button, Container, Input, Select } from '../components/site/ui';
import {
  IconArrowRight,
  IconBell,
  IconCheck,
  IconCheckCircle,
  IconClock,
  IconLink,
  IconLock,
  IconMail,
  IconNote,
  IconPackage,
  IconShield
} from '../components/icons';
import { useCouriers } from '../utils/useCouriers';
import PulseSection from '../components/PulseSection';
import { formatUkPostcode } from '../utils/postcode';
import { trackReportStarted } from '../utils/tracking';

/** The SMS line is appended at render time — the price is configurable, never hardcoded. */
const heroPoints = [
  'No need to contact the courier',
  'We route your case to the right retailer (merchant)',
  'Include tracking, photos and all key details',
  'Free to use'
];

const issueTypes = [
  { value: 'not_arrived', label: "My parcel hasn't arrived yet" },
  { value: 'delivered_not_received', label: "It says delivered, but I didn't receive it" },
  { value: 'damaged', label: 'My delivery was damaged' },
  { value: 'missing_items', label: 'Items are missing from the parcel' },
  { value: 'unsafe_place', label: 'It was left in an unsafe place' }
];

/**
 * Bar colours for the issue split, in the order issues come back ranked.
 * Series colours only — ink is reserved for the figures themselves.
 */
const steps = [
  {
    icon: IconNote,
    title: 'Report your issue',
    description: 'Provide tracking number, merchant, issue type, photos and evidence.'
  },
  {
    icon: IconLink,
    title: 'We send it to the right place',
    description: 'Your case is routed to the merchant who can take action with the courier.'
  },
  {
    icon: IconCheckCircle,
    title: 'Merchant takes action',
    description: 'Refund, reship, replacement or investigate further.'
  },
  {
    icon: IconBell,
    title: 'You get updates',
    description: 'Track progress and receive updates, with optional SMS.'
  }
];

const benefits = [
  {
    icon: IconLink,
    title: 'Goes to the right place',
    description: 'No more contacting couriers directly.'
  },
  { icon: IconClock, title: 'Saves you time', description: 'One simple report.' },
  {
    icon: IconNote,
    title: 'Stronger cases',
    description: 'Includes photos, tracking and a signed Declaration of Receipt where needed.'
  },
  { icon: IconShield, title: 'Free to use', description: 'SMS updates are optional and paid only if you want them.' }
];


/** Label-left / control-right row, as the mockup's report card lays them out. */
const FormRow = ({ label, htmlFor, required, children }) => (
  <div className="grid grid-cols-1 items-center gap-x-4 gap-y-1.5 sm:grid-cols-[9.5rem_1fr]">
    <label htmlFor={htmlFor} className="text-sm font-600 text-ink-800">
      {label}
      {required && (
        <>
          <span className="ml-0.5 text-danger-600" aria-hidden="true">*</span>
          <span className="sr-only"> (required)</span>
        </>
      )}
    </label>
    {children}
  </div>
);

const HomePage = () => {
  const navigate = useNavigate();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courier, setCourier] = useState('');
  const [postcode, setPostcode] = useState('');
  const [merchantName, setMerchantName] = useState('');
  const [issueType, setIssueType] = useState('');
  const couriers = useCouriers();
  const [smsPrice, setSmsPrice] = useState(null);

  // The retailers and couriers named in the strip near the foot of the page.
  // Separate from the Pulse: that is "what happened in the last N days", this
  // is "who we have handled deliveries for", which does not move with a
  // window and must not be ordered by complaint volume.
  const [brands, setBrands] = useState({ merchants: [], couriers: [] });

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/pulse/brands')
      .then(({ data }) => {
        if (!cancelled) {
          setBrands({ merchants: data?.merchants || [], couriers: data?.couriers || [] });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // The SMS add-on price is admin-configurable, so it is read rather than
  // written into the copy — a figure quoted here that disagrees with what a
  // customer is actually charged would be a pricing misstatement.
  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/merchants/pricing')
      .then(({ data }) => {
        if (!cancelled && typeof data?.smsPrice === 'number') setSmsPrice(data.smsPrice);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const smsLabel = smsPrice != null ? `Optional SMS updates — £${smsPrice.toFixed(2)}` : 'Optional SMS updates';

  /**
   * The nine badges in the strip: retailers and couriers interleaved, so the
   * row never degrades into "all five retailers, then all four couriers" when
   * one side is short. Falls back to the courier dropdown's own list while
   * the brands request is in flight, so the strip is never empty.
   */
  const stripBrands = (() => {
    const merchantNames = brands.merchants;
    const courierNames = brands.couriers.length
      ? brands.couriers
      : couriers.filter((c) => c.name?.toLowerCase() !== 'other').map((c) => c.name);

    const out = [];
    for (let i = 0; i < Math.max(merchantNames.length, courierNames.length); i += 1) {
      if (merchantNames[i]) out.push({ key: `m-${merchantNames[i]}`, name: merchantNames[i] });
      if (courierNames[i]) out.push({ key: `c-${courierNames[i]}`, name: courierNames[i] });
    }
    return out.slice(0, 9);
  })();

  const hasMoreBrands = brands.merchants.length + brands.couriers.length > stripBrands.length;

  const handleSubmit = (e) => {
    e.preventDefault();
    // All five are `required` on their inputs too, so the browser's own
    // validation already blocks submission with any of them empty — this is
    // the defense-in-depth copy of that, in case a required attribute is ever
    // dropped from one of them without this guard being updated to match.
    if (trackingNumber && courier && postcode && merchantName && issueType) {
      navigate('/report', { state: { trackingNumber, courier, postcode, merchantName, issueType } });
    }
  };

  return (
    <>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      {/*
        Three layers, first one that loads wins: a real photograph if someone
        drops one at public/hero-doorstep.jpg, otherwise the drawn doorstep
        scene, otherwise the flat gradient. Nothing here can leave a bare box.
      */}
      <section
        className="relative overflow-hidden bg-brand-950 bg-cover bg-center lg:min-h-[calc(100vh-6rem)] lg:flex lg:items-center"
        style={{
          backgroundImage:
            "url('/hero-doorstep.jpg'), url('/hero-doorstep.svg'), linear-gradient(135deg, #01251B 0%, #013328 45%, #045C46 100%)"
        }}
      >
        {/* The artwork carries its own left-hand vignette, so this only tops
            it up — enough for white type to clear AA contrast on the left
            without flattening the doorstep scene on the right. */}
        <div
          className="absolute inset-0 bg-gradient-to-r from-brand-950/90 via-brand-950/70 to-brand-950/40 lg:via-brand-950/55 lg:to-brand-950/15"
          aria-hidden="true"
        />

        <Container width="full" className="relative py-10 sm:py-12">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,27rem)] lg:gap-12">
            {/* Pitch */}
            <div>
              <h1 className="font-display text-[38px] font-800 leading-[1.05] tracking-tight text-white sm:text-[52px]">
                Having a delivery
                <br />
                <span className="text-accent-400">problem?</span>
              </h1>
              <p className="mt-4 text-lg leading-relaxed text-white/80">
                Report it once. We send it to the right place.
              </p>

              <ul className="mt-7 space-y-3">
                {[...heroPoints, smsLabel].map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white">
                      <IconCheck size={12} />
                    </span>
                    <span className="text-[15px] leading-relaxed text-white/90">{point}</span>
                  </li>
                ))}
              </ul>

              {/* Handwritten-style aside, as in the design. Decorative, so it
                  is hidden where there is no room for it rather than wrapped. */}
              <p className="mt-8 hidden max-w-xs items-start gap-2 font-display text-[15px] italic leading-snug text-white/70 xl:flex">
                <svg viewBox="0 0 24 24" className="mt-1 h-5 w-5 shrink-0 -scale-x-100" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 5c-6 0-11 4-13 10" />
                  <path d="M5 11v4h4" />
                </svg>
                We’ll make sure your case reaches the right place.
              </p>
            </div>

            {/* Report card — the first step of the report flow, and the
                target of every "Report an Issue" link in the chrome. */}
            <div id="report" className="scroll-mt-28 rounded-2xl bg-white p-5 shadow-panel sm:p-6">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <IconPackage size={19} />
                </span>
                <h2 className="font-display text-[17px] font-800 tracking-tight text-ink-900">
                  Report a Delivery Issue (FREE)
                </h2>
              </div>

              {/* onChange bubbles from every field: the first edit is the report start. */}
              <form onSubmit={handleSubmit} onChange={trackReportStarted} className="mt-5 space-y-3.5">
                <FormRow label="Choose your courier" htmlFor="courier" required>
                  <Select id="courier" value={courier} onChange={(e) => setCourier(e.target.value)} required>
                    <option value="">Select courier</option>
                    {couriers.map((c) => (
                      <option key={c._id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </FormRow>

                <FormRow label="Tracking number" htmlFor="tracking" required>
                  <Input
                    type="text"
                    id="tracking"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="e.g. H00ABC123456"
                    required
                  />
                </FormRow>

                <FormRow label="Postcode" htmlFor="postcode" required>
                  <Input
                    type="text"
                    id="postcode"
                    value={postcode}
                    onChange={(e) => setPostcode(formatUkPostcode(e.target.value))}
                    placeholder="e.g. SW1A 1AA"
                    required
                  />
                </FormRow>

                <FormRow label="Merchant / Retailer" htmlFor="merchantName" required>
                  <Input
                    type="text"
                    id="merchantName"
                    value={merchantName}
                    onChange={(e) => setMerchantName(e.target.value)}
                    placeholder="e.g. Zara, ASOS, John Lewis"
                    required
                  />
                </FormRow>

                <FormRow label="What’s the problem?" htmlFor="issueType" required>
                  <Select
                    id="issueType"
                    value={issueType}
                    onChange={(e) => setIssueType(e.target.value)}
                    required
                  >
                    <option value="">Select issue type</option>
                    {issueTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>
                </FormRow>

                <Button type="submit" size="lg" className="w-full">
                  Report My Delivery Issue
                  <IconArrowRight size={18} />
                </Button>
              </form>

              <div className="mt-5 grid grid-cols-3 divide-x divide-ink-100 border-t border-ink-100 pt-4 text-center">
                {[
                  { icon: IconLock, lines: ['No account', 'required'] },
                  { icon: IconClock, lines: ['Takes', '2 minutes'] },
                  {
                    icon: IconMail,
                    lines: ['Optional SMS', smsPrice != null ? `updates £${smsPrice.toFixed(2)}` : 'updates']
                  }
                ].map(({ icon: Icon, lines }) => (
                  <div key={lines.join(' ')} className="flex items-center justify-center gap-2 px-1">
                    <Icon size={15} className="shrink-0 text-ink-400" />
                    <span className="text-left text-[11px] leading-tight text-ink-600">
                      {lines[0]}
                      <br />
                      {lines[1]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      <PulseSection />

      {/* ── How ParcelBuddy works ─────────────────────────────────────────── */}
      <section id="how-it-works" className="scroll-mt-24 bg-white py-12 sm:py-14">
        <Container width="full">
          <h2 className="font-display text-2xl font-800 tracking-tight text-ink-900 sm:text-[28px]">
            How Parcel<span className="text-gold-500">Buddy</span> works
          </h2>

          <ol className="mt-7 grid grid-cols-1 gap-x-4 gap-y-7 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const warm = index % 2 === 1;
              return (
                <li key={step.title} className="relative">
                  <div className="flex items-center gap-3">
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-base font-800 text-white ${
                        warm ? 'bg-gold-500 text-brand-900' : 'bg-brand-700'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                        warm ? 'bg-gold-50 text-gold-600' : 'bg-brand-50 text-brand-700'
                      }`}
                    >
                      <Icon size={20} />
                    </span>
                  </div>
                  <h3 className="mt-3.5 font-display text-[15px] font-700 text-ink-900">{step.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{step.description}</p>

                  {index < steps.length - 1 && (
                    <IconArrowRight
                      size={18}
                      className="absolute -right-2 top-2.5 hidden text-ink-300 lg:block"
                      aria-hidden="true"
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </Container>
      </section>

      {/* ── Why use ParcelBuddy ───────────────────────────────────────────── */}
      <section className="bg-brand-50/60 py-12 sm:py-14">
        <Container width="full">
          <h2 className="font-display text-2xl font-800 tracking-tight text-ink-900 sm:text-[28px]">
            Why use Parcel<span className="text-gold-500">Buddy</span>?
          </h2>

          <div className="mt-7 grid grid-cols-1 gap-4 rounded-2xl bg-white p-5 shadow-card sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map(({ icon: Icon, title, description }) => (
              <div key={title} className="flex items-start gap-3.5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-800 text-white">
                  <Icon size={20} />
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-[15px] font-700 text-ink-900">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-ink-600">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Supported retailers and couriers ──────────────────────────────── */}
      <section className="bg-white py-12 sm:py-14">
        <Container width="full">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-xl font-800 tracking-tight text-ink-900 sm:text-2xl">
              Used for deliveries from all major retailers and couriers
            </h2>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 rounded text-sm font-600 text-brand-700 transition-colors hover:text-brand-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
            >
              See all supported merchants and couriers
              <IconArrowRight size={15} />
            </Link>
          </div>

          {/* Real retailers and couriers ParcelBuddy has handled reports
              about — interleaved rather than grouped, so the strip reads as
              one list of businesses and not as two leagues.

              A grid, not a wrapping flex row: with badges of uneven word
              length, wrapping left a single orphan on its own line. */}
          <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-9">
            {stripBrands.map(({ key, name }) => (
              <span
                key={key}
                className="flex items-center justify-center rounded-xl border border-ink-200 bg-white px-2 py-3.5 text-center font-display text-[13px] font-700 tracking-tight text-ink-700"
              >
                {name}
              </span>
            ))}
          </div>
          {hasMoreBrands && <p className="mt-3 text-sm text-ink-500">and many more…</p>}
        </Container>
      </section>

      {/* ── Closing CTA ───────────────────────────────────────────────────── */}
      <section className="bg-brand-900 py-9">
        <Container width="full">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-gold-400">
                <IconPackage size={24} />
              </span>
              <div>
                <h2 className="font-display text-xl font-800 tracking-tight text-white sm:text-2xl">
                  Turn delivery problems into action.
                </h2>
                <p className="mt-1 text-[15px] text-brand-100/75">
                  For customers, merchants and couriers.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button as={Link} to="/report" variant="onDark" size="lg">
                Report an Issue
              </Button>
              <Button as={Link} to="/merchants" variant="gold" size="lg">
                For Merchants
              </Button>
              <Button as={Link} to="/couriers" variant="info" size="lg">
                For Couriers
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};

export default HomePage;
